import { useJwt } from "common-frontend-components";
import { getUserRoles, type RoleName, type UserRoles } from "@/api/accessAPI";
import { getRoleId, getRoleName } from "@/utils/roles";
import { useQuery } from "@tanstack/react-query";
import React, { createContext, useMemo } from "react";

type OnlyOne<T> = {
    [K in keyof T]: { [P in K]: T[K] } & Partial<Record<Exclude<keyof T, K>, never>>;
}[keyof T];

type AllAmIProps = {
    a: RoleName;
    atLeast: RoleName;
    atMost: RoleName;
    not: RoleName;
    anyOf: RoleName[];
    allOf: RoleName[];
    noneOf: RoleName[];
};

// This type will allow only one property at a time from AmIProps
type AmIProps = OnlyOne<AllAmIProps> & { customerId?: number };

type RoleContextType = {
    roles: UserRoles[];
    amI: (props: AmIProps) => boolean;
};

const RoleContext = createContext<RoleContextType>({
    roles: [],
    amI: () => false,
});

function RoleProvider({ children }: { children: React.ReactNode }) {
    const { userId } = useJwt();

    const { data: rawData } = useQuery<UserRoles[]>({
        queryKey: ["myRoles", userId],
        queryFn: () => {
            if (!userId) return Promise.reject("no user Id");
            return getUserRoles(userId);
        },
        enabled: !!userId,
        retry: false,
        refetchInterval: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        placeholderData: [],
    });

    const data = useMemo(() => rawData ?? [], [rawData]);

    const amI = (props: AmIProps) => {
        const { a, atLeast, atMost, not, anyOf, allOf, noneOf, customerId } = props;
        let roles = data;
        if (customerId) {
            roles = roles.filter((role) => role.customerId === customerId);
        }
        if (a) return roles.some((role) => getRoleName(role.roleId) === a);
        if (atLeast) return roles.some((role) => role.roleId >= getRoleId(atLeast));
        if (atMost) return roles.every((role) => role.roleId <= getRoleId(atMost));
        if (not) return !roles.some((role) => getRoleName(role.roleId) === not);
        if (anyOf) return roles.some((role) => anyOf.includes(getRoleName(role.roleId)));
        if (allOf) return allOf.every((role) => roles.some((r) => getRoleName(r.roleId) === role));
        if (noneOf)
            return !noneOf.some((role) => roles.some((r) => getRoleName(r.roleId) === role));
        return false;
    };

    return <RoleContext.Provider value={{ roles: data, amI }}>{children}</RoleContext.Provider>;
}

const AmI = ({ children, ...props }: AmIProps & { children: React.ReactNode }) => {
    const { amI } = React.useContext(RoleContext);
    return amI(props) ? <>{children}</> : null;
};

export { AmI, RoleContext, RoleProvider };
export type { AmIProps, RoleContextType };
